import clsx from 'clsx';
import LoadingScreen from 'components/atoms/LoadingScreen/LoadingScreen';
import PageMetaData, { MetaData } from 'components/atoms/PageMetaData/PageMetaData';
import SuspenseWithIndicator from 'components/atoms/SuspenseWithIndicator/SuspenseWithIndicator';
import ErrorBoundary from 'components/molecules/ErrorBoundary/ErrorBoundary';
import Login from 'components/pages/Login/Login';
import { LOGIN } from 'components/pages/pages';
import CookieBannerMount from 'components/templates/CookieBannerMount/CookieBannerMount';
import ThirdPartyTrackingProvider from 'components/templates/ThirdPartyTrackingProvider/ThirdPartyTrackingProvider';
import TranslationWrapper from 'components/templates/Translation/TranslationWrapper';
import Verisoul from 'components/templates/Verisoul/Verisoul';
import useIsSsr from 'hooks/useIsSsr';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React from 'react';
import { useIsLoggedIn } from '../AuthenticationProvider/AuthenticationProvider';
import PageContentWrapper from './PageContentWrapper';
import wrapperClasses from './PageContentWrapper.module.scss';

interface PropsWithMetaData {
  metaData: MetaData;
}

interface PropsWithoutMetaData {
  withNestedMetaData: true;
}

export type PageOptions = {
  requiresAuthentication?: boolean;
  skipSSR?: boolean;
  fullPageWidth?: boolean;
  noMarginBottomMobile?: boolean;
  unlisted?: boolean;
  loggedInRedirect?: string;
  metaData?: MetaData;
  namespace?: string;
  wrapperClass?: 'finish' | 'normal';
  loadingAnimation?: React.ReactNode;
} & (PropsWithMetaData | PropsWithoutMetaData);
export type Props = {
  children: React.ReactNode;
} & PageOptions;

function Page({
  children,
  skipSSR,
  requiresAuthentication,
  fullPageWidth,
  noMarginBottomMobile,
  unlisted,
  loggedInRedirect,
  metaData,
  namespace,
  wrapperClass = 'normal',
  loadingAnimation,
}: Props) {
  const isLoggedIn = useIsLoggedIn();
  const shouldIndexPage = !unlisted && !requiresAuthentication;
  const router = useRouter();
  const isSsr = useIsSsr();
  const { ready } = useTranslation([namespace, 'common']);

  React.useEffect(() => {
    if (isLoggedIn && loggedInRedirect) {
      router.replace(loggedInRedirect);
    }
  }, [isLoggedIn, router, loggedInRedirect]);

  React.useEffect(() => {
    if (!isSsr && !isLoggedIn && requiresAuthentication) {
      router.replace(LOGIN.path);
    }
  }, [isLoggedIn, isSsr, requiresAuthentication, router]);

  // Return early before the translations-ready gate so LoadingScreen is never remounted
  if (!isSsr && isLoggedIn && loggedInRedirect) {
    return <LoadingScreen />;
  }

  let content = (
    <PageContentWrapper
      fullPageWidth={fullPageWidth}
      noMarginBottomMobile={noMarginBottomMobile}
      className={clsx(wrapperClasses[wrapperClass])}
    >
      {children}
    </PageContentWrapper>
  );
  if ((requiresAuthentication || skipSSR) && isSsr) {
    // We don't wanna prerender any content if the content is not public
    content = <LoadingScreen />;
  } else if (!isLoggedIn && requiresAuthentication) {
    content = (
      <TranslationWrapper namespace={'common'}>
        <Login />
      </TranslationWrapper>
    );
  }

  return ready ? (
    <>
      <PageMetaData metaData={metaData} shouldIndexPage={shouldIndexPage} />
      <ErrorBoundary>
        <SuspenseWithIndicator fallback={loadingAnimation}>
          <Verisoul />
          <ThirdPartyTrackingProvider />
          <CookieBannerMount />
          {content}
        </SuspenseWithIndicator>
      </ErrorBoundary>
    </>
  ) : (
    // We don't want to render the page meta data if the translations are not ready yet
    <LoadingScreen />
  );
}

const MemoizedPage = React.memo<Props>(Page);

export default MemoizedPage;
